import React from 'react';

import { Description, Field, Label } from '@oysterjs/ui/fieldset';
import { Switch, SwitchField } from '@oysterjs/ui/switch';

import { OutputTemplatePicker } from '~/components/cara-input/output-template-picker';
import { type CaraInputTemplatePicker } from '~/components/cara-input/cara-input';
import { useCaraAttachments } from '~/components/cara-input/use-cara-attachments';
import { type CaraAttachment } from '~/components/cara-input/cara-attachment';
import {
  templateWorkspaceOptionsFromAttachments,
  type WorkspaceTemplateCreateOptions
} from './presets.utils';

export type PresetRunOutputTemplatePickers = {
  customFormPicker?: CaraInputTemplatePicker;
  wordTemplatePicker?: CaraInputTemplatePicker;
  excelTemplatePicker?: CaraInputTemplatePicker;
  htmlTemplatePicker?: CaraInputTemplatePicker;
};

export type PresetRunOutputTemplateWorkspaceOptions = WorkspaceTemplateCreateOptions & {
  omitPresetOutputTemplates: true;
};

export const usePresetRunOutputTemplates = (initialOutputTemplates?: CaraAttachment[]) => {
  const templateAttachments = useCaraAttachments();
  const [useOutputTemplates, setUseOutputTemplates] = React.useState(
    () => (initialOutputTemplates?.length ?? 0) > 0
  );

  React.useEffect(() => {
    templateAttachments.clear();
    if (initialOutputTemplates) {
      templateAttachments.add(...initialOutputTemplates);
      setUseOutputTemplates(initialOutputTemplates.length > 0);
    }
  }, [initialOutputTemplates]);

  const getWorkspaceOptions = React.useCallback((): PresetRunOutputTemplateWorkspaceOptions => {
    return {
      omitPresetOutputTemplates: true,
      ...templateWorkspaceOptionsFromAttachments(
        useOutputTemplates,
        templateAttachments.attachments
      )
    };
  }, [templateAttachments.attachments, useOutputTemplates]);

  return {
    useOutputTemplates,
    setUseOutputTemplates,
    templateAttachments,
    getWorkspaceOptions
  };
};

export const PresetRunOutputTemplatesField = (props: {
  useOutputTemplates: boolean;
  onUseOutputTemplatesChange: (next: boolean) => void;
  attachments: CaraAttachment[];
  onAdd: (...next: CaraAttachment[]) => void;
  onRemove: (id: string) => void;
  outputTemplatePickers: PresetRunOutputTemplatePickers;
}) => (
  <>
    <SwitchField>
      <Label>Fill Templates or Forms</Label>
      <Description>
        Specify any templates or forms from the Library that Cara should fill when generating
        deliverables for this run. Turning this off clears them for this run.
      </Description>
      <Switch
        color="sky"
        checked={props.useOutputTemplates}
        onChange={props.onUseOutputTemplatesChange}
      />
    </SwitchField>
    {props.useOutputTemplates && (
      <Field>
        <OutputTemplatePicker
          attachments={props.attachments}
          onAdd={props.onAdd}
          onRemove={props.onRemove}
          buttonLabel="Add custom template or form"
          buttonAriaLabel="Add custom template or form"
          {...props.outputTemplatePickers}
        />
      </Field>
    )}
  </>
);
